import { useCartPageSearchQuery } from "@faire/add-to-cart/hooks/useCartPageSearchQuery";
import { useFaireSourceEligibility } from "@faire/add-to-cart/lib/FaireSource/useFaireSourceEligibility";
import { useInvalidateServerCart } from "@faire/add-to-cart/lib/ServerCart/useInvalidateServerCart";
import { getCartToken } from "@faire/add-to-cart/serialized-data/getCartToken";
import {
  useInvalidateQuery as useInvalidateCartQuery,
  useInvalidateInfiniteQuery as useInvalidateInviniteCartQuery,
} from "@faire/web-api--source/endpoints/www/api/v2/carts/cartToken/get-hooks";
import { route as cartRoute } from "@faire/web-api--source/routes/www/cart";
import { Storage } from "@faire/web/common/Storage";
import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

import {
  ACTIVE_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY,
  SAVED_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY,
} from "./consts";
import { useCartQueryParams } from "./useCartQueryParams";

type InvalidateCartArg = {
  savedForLater: boolean;
  hasFbfBenefits: boolean;
  query: string | undefined;
};

export const useInvalidateCart = () => {
  const location = useLocation();
  const buildQueryParams = useCartQueryParams();
  const invalidateCartQuery = useInvalidateCartQuery();
  const invalidateInfiniteCartQuery = useInvalidateInviniteCartQuery();
  const searchQuery = useCartPageSearchQuery();

  const isFaireSourceEligible = useFaireSourceEligibility();

  const query = useMemo(() => {
    return location.pathname === cartRoute ? searchQuery : undefined;
  }, [location.pathname, searchQuery]);

  const invalidateCartRequest = useCallback(
    async ({ savedForLater, hasFbfBenefits, query }: InvalidateCartArg) => {
      const args = [
        getCartToken(),
        buildQueryParams({ savedForLater, hasFbfBenefits, query }),
        undefined,
        { refetchType: "all" },
      ] as const;
      await Promise.all([
        invalidateInfiniteCartQuery(...args),
        invalidateCartQuery(...args),
      ]);
    },
    [buildQueryParams, invalidateCartQuery, invalidateInfiniteCartQuery]
  );

  const {
    invalidateActiveServerCart,
    invalidateFaireSourceServerCart,
    invalidateSavedServerCart,
  } = useInvalidateServerCart();

  const invalidateCart = useCallback(
    async (savedForLater: boolean, hasFbfBenefits: boolean) => {
      const promises: Promise<void>[] = [];
      if (hasFbfBenefits) {
        promises.push(invalidateFaireSourceServerCart());
      } else if (savedForLater) {
        promises.push(invalidateSavedServerCart());
      } else {
        promises.push(invalidateActiveServerCart());
      }
      const queries = getQueriesFromStorage(savedForLater);
      if (queries.length > 0) {
        queries.forEach((q) => {
          promises.push(
            invalidateCartRequest({ savedForLater, hasFbfBenefits, query: q })
          );
        });
        const key = savedForLater
          ? SAVED_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY
          : ACTIVE_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY;
        // All search queries have been dealt with
        Storage.session.removeItem(key);
      } else {
        promises.push(
          invalidateCartRequest({ savedForLater, hasFbfBenefits, query })
        );
      }
      await Promise.all(promises);
    },
    [
      invalidateActiveServerCart,
      invalidateCartRequest,
      invalidateSavedServerCart,
      query,
    ]
  );

  const invalidateActiveCart = useCallback(async () => {
    await invalidateCart(false, false);
  }, [invalidateCart]);

  const invalidateFaireSourceCart = useCallback(async () => {
    await invalidateCart(false, true);
  }, [invalidateCart]);

  const invalidateSavedCart = useCallback(async () => {
    await invalidateCart(true, false);
  }, [invalidateCart]);

  const invalidateEntireCart = useCallback(
    () =>
      Promise.all([
        invalidateActiveCart(),
        ...(isFaireSourceEligible ? [invalidateFaireSourceCart()] : []),
        invalidateSavedCart(),
      ]),
    [
      invalidateActiveCart,
      invalidateFaireSourceCart,
      invalidateSavedCart,
      isFaireSourceEligible,
    ]
  );

  return {
    invalidateActiveCart,
    invalidateSavedCart,
    invalidateEntireCart,
  };
};

const getQueriesFromStorage = (savedForLater: boolean): string[] => {
  const key = savedForLater
    ? SAVED_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY
    : ACTIVE_CART_PAGE_SEARCH_QUERIES_STORAGE_KEY;
  const queries = Storage.session.getItem(key);
  return queries ? JSON.parse(queries) : [];
};
